import {
  DEFAULT_POLICY_EXISTED_ERROR,
  DUPLICATE_POLICY_NAME_ERROR,
  VALIDATE_DEFAULT_POLICY_ERROR,
} from "@constants/licensePolicy";
import { EXCEL_I18N_ENABLED } from "@constants/sbom";

export function flattenSbomErrorParams(error) {
  const nested =
    error?.params && typeof error.params === "object" && !Array.isArray(error.params)
      ? error.params
      : {};
  const params = { ...nested };
  const row = params.row ?? error?.row;
  const column = params.column ?? error?.column;
  if (row !== undefined && row !== null && row !== "") {
    params.row = Number(row);
  }
  if (column !== undefined && column !== null && column !== "") {
    params.column = Number(column);
  }
  return params;
}

export function formatSbomErrorLocation(language, row, column) {
  const locale = (language || "en").toLowerCase();
  const hasRow = Number.isFinite(row);
  const hasColumn = Number.isFinite(column);
  if (locale.startsWith("ko")) {
    if (hasRow && hasColumn) return `${row}행, ${column}열`;
    if (hasColumn) return `${column}열`;
    if (hasRow) return `${row}행`;
    return "";
  }
  if (locale.startsWith("ja")) {
    if (hasRow && hasColumn) return `${row}行目、${column}列目`;
    if (hasColumn) return `${column}列目`;
    if (hasRow) return `${row}行目`;
    return "";
  }
  if (hasRow && hasColumn) return `row ${row}, column ${column}`;
  if (hasColumn) return `column ${column}`;
  if (hasRow) return `row ${row}`;
  return "";
}

export function formatSbomApiError(t, error, language) {
  const messageKey = error?.messageKey;
  if (!messageKey) {
    return null;
  }
  const params = flattenSbomErrorParams(error);
  const isExcelKey = messageKey.startsWith("sbom.excel.");
  const effectiveLanguage = isExcelKey && !EXCEL_I18N_ENABLED ? "en" : language;
  if (messageKey === "sbom.excel.invalid-field-name") {
    params.location = formatSbomErrorLocation(effectiveLanguage, params.row, params.column);
  }
  if (messageKey === "sbom.error.unsupported-version" && !params.format) {
    params.format = "";
  }
  const resolvedKey =
    messageKey === "sbom.excel.invalid-field-name" && EXCEL_I18N_ENABLED
      ? "sbom.excel.invalid-field-name-i18n"
      : messageKey;
  if (isExcelKey && !EXCEL_I18N_ENABLED) {
    return t(resolvedKey, { ...params, lng: "en" });
  }
  return t(resolvedKey, params);
}

export function extractErrorCode(error, defaultCode = null) {
  try {
    let code = null;

    // Case: Axios error
    if (error.response && error.response.data && error.response.data.code) {
      code = error.response.data.code;
    }

    // Return with prefix, fallback to defaultCode
    return code ? `errorcode.${code}` : (defaultCode ? `${defaultCode}` : null);
  } catch (e) {
    return defaultCode ? `errorcode.${defaultCode}` : null;
  }
}

export function extractServerMessage(error) {
  const responseData = error?.response?.data;
  if (typeof responseData === "string") {
    return responseData;
  }
  if (responseData && typeof responseData === "object") {
    return responseData.message || responseData.error || responseData.detail || "";
  }
  return "";
}

export function isExternalApiTimeoutError(error) {
  const code = error?.response?.data?.code;
  if (code === "20301") {
    return true;
  }

  const message = extractServerMessage(error).toLowerCase();
  return (
    message.includes("timed out") ||
    message.includes("timeoutexception") ||
    message.includes("connecttimeoutexception")
  );
}

export function isExternalApiCertificateError(error) {
  const code = error?.response?.data?.code;
  if (code === "20302") {
    return true;
  }

  const message = extractServerMessage(error).toLowerCase();
  return (
    message.includes("certificate validation failed") ||
    message.includes("unable to find valid certification path") ||
    message.includes("trust anchors")
  );
}

export function getExternalApiErrorMessage(error, t, defaultCode = "common.error-occurred") {
  if (isExternalApiTimeoutError(error)) {
    if (error?.response?.data?.code === "20301") {
      return t("errorcode.20301");
    }
    return t("error-api.timeout");
  }

  if (isExternalApiCertificateError(error)) {
    if (error?.response?.data?.code === "20302") {
      return t("errorcode.20302");
    }
    return t("error-api.certificate");
  }

  const errorCode = extractErrorCode(error, defaultCode);
  return t(errorCode);
}

export function getVerificationUploadErrorMessage(error, t) {
  if (error?.response?.data?.code === "20300" && error?.response?.data?.additionalMessage) {
    return error.response.data.additionalMessage;
  }

  return getExternalApiErrorMessage(error, t);
}

export function getClarityAirVerificationErrorMessage(error, t) {
  if (isExternalApiTimeoutError(error)) {
    if (error?.response?.data?.code === "20301") {
      return t("errorcode.20301");
    }
    return t("error-api.timeout");
  }

  if (isExternalApiCertificateError(error)) {
    if (error?.response?.data?.code === "20302") {
      return t("errorcode.20302");
    }
    return t("error-api.certificate");
  }

  const serverMessage = extractServerMessage(error);
  if (serverMessage.includes("API URL is not configured or empty")) {
    return t("project.verification-request.clarity-air-api-url-not-configured");
  }
  if (serverMessage.includes("Personal Access Token (PAT) is not configured or empty")) {
    return t("project.verification-request.clarity-air-pat-not-configured");
  }
  if (serverMessage.includes("Invalid ClarityAIR default workspace")) {
    return t("preferences.clarity-air-default-workspace-invalid");
  }

  return getExternalApiErrorMessage(error, t);
}

export function handleLicensePolicyError(error, t, showErrorToast) {
  try {
    if (error.response && error.response.data) {
      const { additionalMessage } = error.response.data;

      // Get i18n message for the error code
      const i18nKey = extractErrorCode(error);
      let toastMessage = t(i18nKey || "errorcode.00001");

      // Append additionalMessage if available (keep original value from API)
      if (additionalMessage) {
        toastMessage = `${toastMessage}: ${additionalMessage}`;
      }

      showErrorToast(t("common.error"), toastMessage);
      return;
    }
  } catch (e) {
    console.warn("Error parsing license policy error response:", e);
  }

  showErrorToast(t("common.error"), t("errorcode.00001"));
}

/**
 * Handles license policy create/update/import API errors.
 * Returns true when a user-facing toast was shown.
 */
export function handleLicensePolicyMutationError(error, t, showErrorToast, fallbackMessage = null) {
  if (error?.response?.data?.code?.startsWith("204")) {
    handleLicensePolicyError(error, t, showErrorToast);
    return true;
  }

  const serverMessage = extractServerMessage(error);

  if (serverMessage.includes(DUPLICATE_POLICY_NAME_ERROR)) {
    showErrorToast(t("common.error"), t("license-policy.policy-name-duplicated"));
    return true;
  }
  if (
    serverMessage.includes(VALIDATE_DEFAULT_POLICY_ERROR) ||
    serverMessage.includes("At least one policy must be set as Default Policy")
  ) {
    showErrorToast(t("common.error"), t("license-policy.validate-default-policy-error"));
    return true;
  }
  if (
    serverMessage.includes(DEFAULT_POLICY_EXISTED_ERROR) ||
    serverMessage.includes("Default policy already exists")
  ) {
    showErrorToast(t("common.error"), t("license-policy.default-policy-existed-error"));
    return true;
  }

  if (fallbackMessage) {
    showErrorToast(t("common.error"), fallbackMessage);
    return true;
  }

  return false;
}
